import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { COURSE_GROUP_FIELD_TYPE, coursegroupsApi } from "api";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { MaterialIconSelect } from "components/atoms";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { PaziaFormButton, PaziaInput, PaziaTextarea } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { QUERIES_KEYS } from "queries";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useToaster } from "utils";
import { CourseGroupFieldFormValues, CourseGroupFieldSchema } from "./SingleCourseGroupFieldForm";

interface CreateCourseGroupFieldModalProps {
  onCloseRequest: () => void;
  isOpen: boolean;
  type: number;
  courseGroupId: number;
}

export const CreateCourseGroupFieldModal = ({
  onCloseRequest,
  isOpen,
  type,
  courseGroupId,
}: CreateCourseGroupFieldModalProps) => {
  const methods = useForm<CourseGroupFieldFormValues>({
    resolver: yupResolver(CourseGroupFieldSchema),
  });

  const { reset } = methods;
  const onClose = () => {
    reset();
    onCloseRequest();
  };
  return (
    <PaziaModal id="create-cgf-modal" onCloseRequest={onClose} isOpen={isOpen} size="xl">
      <FormProvider {...methods}>
        <ModalPadding>
          <ModalHeader>Dodaj nowe pole</ModalHeader>
          <Form courseGroupId={courseGroupId} onCloseRequest={onClose} type={type} />
        </ModalPadding>
      </FormProvider>
    </PaziaModal>
  );
};

const Form = ({
  courseGroupId,
  onCloseRequest,
  type,
}: {
  courseGroupId: number;
  type: number;
  onCloseRequest: () => void;
}) => {
  const { showError, showSuccess } = useToaster();
  const methods = useFormContext<CourseGroupFieldFormValues>();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.COURSE_GROUP_BY_ID(Number(courseGroupId)),
    });
  };

  const onSubmit = async (data: CourseGroupFieldFormValues) => {
    try {
      const requestData = { ...data, icon: data.icon ? `<span class="material-icons">${data.icon}</span>` : "" };
      await coursegroupsApi.createField(courseGroupId, { ...requestData, type });
      showSuccess("Utworzono grupę kursów");
      onCloseRequest();
    } catch (error) {
      showError("Nie udało się zaktualizować grupy kursów");
    } finally {
      invalidate();
    }
  };

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  console.log({ errors });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <PaziaInput label="Nazwa pola" name="name" className="w-full" />
        <MaterialIconSelect name="icon" label="Ikona" className="w-full" />

        <div>
          <label className="mb-2 block text-sm font-medium text-slate-800 dark:text-slate-100">Treść</label>
          {type === COURSE_GROUP_FIELD_TYPE.DESCRIPTION && <PaziaTextarea name="content" rows={6} />}
          {type === COURSE_GROUP_FIELD_TYPE.DETAILED_INFORMATION && <PaziaFromQuill name="content" />}
          {/* {type === COURSE_GROUP_FIELD_TYPE.PAYMENTS && <PaziaFromQuill name="content" />} */}
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 dark:border-slate-600 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCloseRequest}
          className="order-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 sm:order-1"
        >
          Anuluj
        </button>
        <PaziaFormButton title="Utwórz pole" type="success" className="order-1 sm:order-2" />
      </div>
    </form>
  );
};
